import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Alert, AlertDescription } from './ui/alert';
import {
  ArrowLeft,
  Star,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  User,
  CheckCircle,
  AlertCircle,
  Target,
  Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ComparacaoBifocal = () => {
  const { parent_id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingIA, setLoadingIA] = useState(false);
  const [analiseIA, setAnaliseIA] = useState(null);
  const [dados, setDados] = useState({
    autoavaliacao: null,
    avaliacao_gestor: null,
    employee: null,
    evaluator: null,
    cycle: null
  });
  const [competencias, setCompetencias] = useState([]);

  useEffect(() => {
    loadComparacao();
  }, [parent_id]);

  const loadComparacao = async () => {
    try {
      setLoading(true);
      
      // Buscar dados da avaliação bifocal
      const response = await axios.get(
        `${API}/api/performance/evaluations/bifocal/${parent_id}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      setDados(response.data);
      
      // Buscar competências para obter os nomes
      const compResponse = await axios.get(
        `${API}/api/performance/competencies`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      setCompetencias(compResponse.data || []);
      
      // Buscar análise com IA se já existir (sem bloquear o carregamento)
      try {
        const analiseResponse = await axios.post(
          `${API}/api/performance/evaluations/bifocal/${parent_id}/analyze`,
          {},
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        
        if (analiseResponse.data.cached) {
          console.log('✅ Análise com IA carregada do cache');
          setAnaliseIA(analiseResponse.data.analise);
        }
      } catch (error) {
        console.log('ℹ️ Análise com IA ainda não gerada');
      }
      
    } catch (error) {
      console.error('Erro ao carregar comparação:', error);
      toast.error('Erro ao carregar comparação bifocal');
    } finally {
      setLoading(false);
    }
  };

  const calcularDivergencia = (ratingSelf, ratingSuperior) => {
    const diff = Math.abs(ratingSelf - ratingSuperior);
    if (diff === 0) return { nivel: 'convergente', cor: 'text-green-600', bg: 'bg-green-50' };
    if (diff === 1) return { nivel: 'leve', cor: 'text-yellow-600', bg: 'bg-yellow-50' };
    if (diff === 2) return { nivel: 'moderada', cor: 'text-orange-600', bg: 'bg-orange-50' };
    return { nivel: 'alta', cor: 'text-red-600', bg: 'bg-red-50' };
  };

  const calcularMedias = () => {
    const { autoavaliacao, avaliacao_gestor } = dados;
    
    if (!autoavaliacao?.responses || !avaliacao_gestor?.responses) {
      return { mediaSelf: 0, mediaSuperior: 0, diferencaMedia: 0 };
    }

    const somasSelf = autoavaliacao.responses.reduce((acc, r) => acc + (r.rating || 0), 0);
    const somasSuperior = avaliacao_gestor.responses.reduce((acc, r) => acc + (r.rating || 0), 0);

    const mediaSelf = autoavaliacao.responses.length > 0 ? somasSelf / autoavaliacao.responses.length : 0;
    const mediaSuperior = avaliacao_gestor.responses.length > 0 ? somasSuperior / avaliacao_gestor.responses.length : 0;

    return {
      mediaSelf: mediaSelf.toFixed(2),
      mediaSuperior: mediaSuperior.toFixed(2),
      diferencaMedia: (mediaSelf - mediaSuperior).toFixed(2)
    };
  };

  const identificarMaioresDiscrepancias = () => {
    const { autoavaliacao, avaliacao_gestor } = dados;
    
    if (!autoavaliacao?.responses || !avaliacao_gestor?.responses) {
      return [];
    }

    const discrepancias = [];

    autoavaliacao.responses.forEach(selfResp => {
      const superiorResp = avaliacao_gestor.responses.find(
        r => r.competency_id === selfResp.competency_id
      );

      if (superiorResp) {
        const diff = Math.abs(selfResp.rating - superiorResp.rating);
        if (diff >= 2) {
          discrepancias.push({
            competency_id: selfResp.competency_id,
            competency_name: selfResp.competency_name || 'Competência',
            ratingSelf: selfResp.rating,
            ratingSuperior: superiorResp.rating,
            diferenca: diff
          });
        }
      }
    });

    return discrepancias.sort((a, b) => b.diferenca - a.diferenca);
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: 'Abaixo do Esperado',
      2: 'Parcialmente Atende',
      3: 'Atende',
      4: 'Supera',
      5: 'Excepcional'
    };
    return labels[rating] || 'N/A';
  };

  const getCompetencyName = (competency_id) => {
    const comp = competencias.find(c => c.id === competency_id);
    return comp ? comp.name : 'Competência';
  };

  const renderStars = (rating, color = 'text-yellow-500') => {
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={`h-4 w-4 ${i <= rating ? `fill-current ${color}` : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const gerarAnaliseIA = async (forceRegenerate = false) => {
    try {
      setLoadingIA(true);
      
      if (forceRegenerate) {
        // Deletar análise antiga antes de gerar nova
        toast.info('Regenerando análise com IA...');
        // O backend já verifica se existe, mas vamos forçar deletar
      } else {
        toast.info('Gerando análise com IA...');
      }
      
      const response = await axios.post(
        `${API}/api/performance/evaluations/bifocal/${parent_id}/analyze`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      setAnaliseIA(response.data.analise);
      
      if (response.data.cached && !forceRegenerate) {
        toast.success('Análise carregada do histórico!');
      } else {
        toast.success('Análise gerada com sucesso!');
      }
      
    } catch (error) {
      console.error('Erro ao gerar análise:', error);
      toast.error('Erro ao gerar análise com IA');
    } finally {
      setLoadingIA(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Carregando comparação...</span>
        </div>
      </div>
    );
  }

  if (!dados.autoavaliacao || !dados.avaliacao_gestor) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Não foi possível carregar as avaliações para comparação. Verifique se ambas as avaliações (autoavaliação e avaliação do gestor) foram finalizadas.
          </AlertDescription>
        </Alert>
        <Button onClick={() => navigate(-1)} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
      </div>
    );
  }

  const { mediaSelf, mediaSuperior, diferencaMedia } = calcularMedias();
  const maioresDiscrepancias = identificarMaioresDiscrepancias();
  const { autoavaliacao, avaliacao_gestor } = dados;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Comparação Bifocal</h1>
              <p className="text-gray-600">
                Autoavaliação vs Avaliação do Gestor - {dados.employee?.nome || 'Colaborador'}
              </p>
            </div>
          </div>
          <Badge variant="outline" className="text-lg px-4 py-2">
            Ciclo: {dados.cycle?.nome || 'N/A'}
          </Badge>
        </div>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Média Autoavaliação</CardTitle>
              <User className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{mediaSelf}</div>
              <p className="text-xs text-gray-600">De 5.0</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Média Gestor</CardTitle>
              <Star className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{mediaSuperior}</div>
              <p className="text-xs text-gray-600">De 5.0</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Diferença Média</CardTitle>
              {parseFloat(diferencaMedia) > 0 ? (
                <TrendingUp className="h-4 w-4 text-green-600" />
              ) : parseFloat(diferencaMedia) < 0 ? (
                <TrendingDown className="h-4 w-4 text-red-600" />
              ) : (
                <Minus className="h-4 w-4 text-gray-600" />
              )}
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${
                parseFloat(diferencaMedia) > 0 ? 'text-green-600' :
                parseFloat(diferencaMedia) < 0 ? 'text-red-600' : 'text-gray-600'
              }`}>
                {diferencaMedia > 0 ? '+' : ''}{diferencaMedia}
              </div>
              <p className="text-xs text-gray-600">
                {parseFloat(diferencaMedia) > 0 ? 'Autoavaliação maior' :
                 parseFloat(diferencaMedia) < 0 ? 'Gestor avaliou maior' : 'Convergente'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Maiores Discrepâncias */}
        {maioresDiscrepancias.length > 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-orange-600" />
                Competências com Maior Discrepância (≥ 2 pontos)
              </CardTitle>
              <CardDescription>
                Estas competências merecem atenção especial na reunião de feedback
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {maioresDiscrepancias.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{item.competency_name}</p>
                      <p className="text-sm text-gray-600">
                        Diferença: {item.diferenca} ponto(s)
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Autoavaliação</p>
                        <p className="text-lg font-bold text-blue-600">{item.ratingSelf}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Gestor</p>
                        <p className="text-lg font-bold text-purple-600">{item.ratingSuperior}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Comparação Detalhada por Competência */}
        <Card>
          <CardHeader>
            <CardTitle>Comparação Detalhada por Competência</CardTitle>
            <CardDescription>
              Visualize lado a lado as avaliações de cada competência
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {autoavaliacao.responses.map((selfResp, idx) => {
                const superiorResp = avaliacao_gestor.responses.find(
                  r => r.competency_id === selfResp.competency_id
                );

                if (!superiorResp) return null;

                const divergencia = calcularDivergencia(selfResp.rating, superiorResp.rating);

                return (
                  <div key={idx} className={`border-2 rounded-lg p-4 ${divergencia.bg} border-gray-200`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg text-gray-900">
                          {getCompetencyName(selfResp.competency_id)}
                        </h4>
                        <Badge variant="outline" className={`mt-1 ${divergencia.cor}`}>
                          Divergência: {divergencia.nivel}
                        </Badge>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Autoavaliação */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-blue-600" />
                          <h5 className="font-semibold text-blue-600">Autoavaliação</h5>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Nota:</p>
                          <div className="flex items-center gap-2">
                            {renderStars(selfResp.rating, 'text-blue-600')}
                            <span className="text-lg font-bold text-blue-600">{selfResp.rating}/5</span>
                            <span className="text-sm text-gray-500">({getRatingLabel(selfResp.rating)})</span>
                          </div>
                        </div>

                        {selfResp.comments && (
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Comentários:</p>
                            <p className="text-sm text-gray-700 bg-white p-2 rounded">
                              {selfResp.comments}
                            </p>
                          </div>
                        )}

                        {selfResp.evidence && (
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Evidências:</p>
                            <p className="text-sm text-gray-700 bg-white p-2 rounded">
                              {selfResp.evidence}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Avaliação do Gestor */}
                      <div className="space-y-3 border-l-2 border-gray-300 pl-6">
                        <div className="flex items-center gap-2">
                          <Star className="h-4 w-4 text-purple-600" />
                          <h5 className="font-semibold text-purple-600">Avaliação do Gestor</h5>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Nota:</p>
                          <div className="flex items-center gap-2">
                            {renderStars(superiorResp.rating, 'text-purple-600')}
                            <span className="text-lg font-bold text-purple-600">{superiorResp.rating}/5</span>
                            <span className="text-sm text-gray-500">({getRatingLabel(superiorResp.rating)})</span>
                          </div>
                        </div>

                        {superiorResp.comments && (
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Comentários:</p>
                            <p className="text-sm text-gray-700 bg-white p-2 rounded">
                              {superiorResp.comments}
                            </p>
                          </div>
                        )}

                        {superiorResp.evidence && (
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Evidências:</p>
                            <p className="text-sm text-gray-700 bg-white p-2 rounded">
                              {superiorResp.evidence}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Comentários Gerais */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                Reflexões do Colaborador
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                {autoavaliacao.overall_comments || 'Sem comentários gerais.'}
              </p>
              {autoavaliacao.development_priorities && autoavaliacao.development_priorities.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-600 mb-2">Áreas de Desenvolvimento:</p>
                  <div className="flex flex-wrap gap-2">
                    {autoavaliacao.development_priorities.map((area, idx) => (
                      <Badge key={idx} variant="outline">{area}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-purple-600" />
                Avaliação do Gestor
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                {avaliacao_gestor.overall_comments || 'Sem comentários gerais.'}
              </p>
              {avaliacao_gestor.development_priorities && avaliacao_gestor.development_priorities.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-600 mb-2">Prioridades de Desenvolvimento:</p>
                  <div className="flex flex-wrap gap-2">
                    {avaliacao_gestor.development_priorities.map((area, idx) => (
                      <Badge key={idx} variant="outline">{area}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Análise com IA */}
        <Card className="border-purple-200 bg-purple-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-purple-600" />
                Análise Técnica com IA
              </CardTitle>
              <Button
                onClick={() => gerarAnaliseIA(!analiseIA)}
                disabled={loadingIA}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {loadingIA ? 'Gerando...' : (analiseIA ? '🔄 Regenerar Análise' : '✨ Gerar Análise com IA')}
              </Button>
            </div>
            <CardDescription>
              Insights automáticos comparando as percepções do colaborador e gestor
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingIA ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <span className="ml-3 text-purple-700">Analisando com IA...</span>
              </div>
            ) : analiseIA ? (
              <div className="prose max-w-none">
                <div className="bg-white p-4 rounded-lg whitespace-pre-wrap text-gray-800">
                  {analiseIA}
                </div>
                <div className="mt-2 text-xs text-gray-500 italic">
                  💡 Esta análise foi salva automaticamente e estará disponível sempre que você acessar esta comparação.
                </div>
              </div>
            ) : (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Clique em "Gerar Análise com IA" para obter insights técnicos sobre as divergências e sugestões de plano de ação. A análise será salva automaticamente.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Status da Reunião de Feedback */}
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              Próxima Etapa: Reunião de Feedback
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                <p className="font-semibold mb-2">Ambas as avaliações foram finalizadas!</p>
                <p className="text-sm">
                  O próximo passo é agendar uma reunião de feedback entre o colaborador e o gestor para:
                </p>
                <ul className="text-sm mt-2 ml-4 list-disc space-y-1">
                  <li>Discutir as percepções de cada um sobre as competências</li>
                  <li>Alinhar expectativas para o próximo período</li>
                  <li>Definir plano de desenvolvimento conjunto</li>
                  <li>Resolver divergências identificadas nesta comparação</li>
                </ul>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Botões de Ação */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex-1"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          
          <Button
            onClick={() => {
              toast.info('Funcionalidade de agendamento de reunião em desenvolvimento');
            }}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            <Calendar className="mr-2 h-4 w-4" />
            Agendar Reunião de Feedback
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ComparacaoBifocal;
